import { CalendarMonth } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { Timespan, TimespanPredefinedType } from '@thingslog/repositories';
import { cn } from '@thingslog/ui-components';
import { format, isValid, parseISO } from 'date-fns';
import React, { FC } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

interface Props {
  timespan: Timespan | null | undefined;
  onClick: () => void;
  title?: string;
  isInheritedFromDashboard?: boolean;
  className?: string;
}

const predefinedTimespanLabelKeys: Partial<Record<TimespanPredefinedType, string>> = {
  today: 'dashboard_timespan_today',
  yesterday: 'dashboard_timespan_yesterday',
  'this-week': 'dashboard_timespan_this_week',
  'this-month': 'dashboard_timespan_this_month',
  'this-year': 'dashboard_timespan_this_year',
  'past-1d': 'dashboard_timespan_past_1d',
  'past-1w': 'dashboard_timespan_past_1w',
  'past-1m': 'dashboard_timespan_past_1m',
  'past-1y': 'dashboard_timespan_past_1y',
};

const predefinedTimespanFallbacks: Partial<Record<TimespanPredefinedType, string>> = {
  today: 'Today',
  yesterday: 'Yesterday',
  'this-week': 'This Week',
  'this-month': 'This Month',
  'this-year': 'This Year',
  'past-1d': 'Past 1 Day',
  'past-1w': 'Past 1 Week',
  'past-1m': 'Past 1 Month',
  'past-1y': 'Past 1 Year',
};

const formatDate = (value: string): string => {
  const date = parseISO(value);

  if (!isValid(date)) {
    return value;
  }

  return format(date, 'dd/MM/yyyy');
};

export const getTimespanLabel = (timespan: Timespan | null | undefined, t: TFunction): string => {
  if (!timespan) {
    return t('dashboard_period', 'Dashboard Period');
  }

  if (timespan.type === 'predefined') {
    const labelKey = predefinedTimespanLabelKeys[timespan.name];
    const fallback = predefinedTimespanFallbacks[timespan.name] ?? timespan.name;
    return labelKey ? t(labelKey, fallback) : fallback;
  }

  return `${formatDate(timespan.fromDate)} - ${formatDate(timespan.toDate)}`;
};

export const TimeframeChip: FC<Props> = ({
  timespan,
  onClick,
  title,
  isInheritedFromDashboard = false,
  className,
}: Props) => {
  const { t } = useTranslation();
  const label = getTimespanLabel(timespan, t);

  return (
    <Tooltip title={title ?? t('dashboard_edit_timeframe', 'Edit timeframe')}>
      <button
        type="button"
        className={cn(
          'inline-flex max-w-full shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-solid border-cyan-200 bg-cyan-50 px-2 py-1 text-xs font-medium text-cyan-900 transition-colors hover:border-cyan-300 hover:bg-cyan-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/30',
          className
        )}
        onClick={onClick}
      >
        <CalendarMonth fontSize="small" className="text-cyan-700" />
        <span className="truncate">
          {isInheritedFromDashboard
            ? t('dashboard_inherited_timeframe_label', 'Dashboard: {{label}}', { label })
            : label}
        </span>
      </button>
    </Tooltip>
  );
};
